import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../schema.dexie";
import { getConfig, salvarConfig } from "../db/queries";
import { PASSO_ESCADA_LABEL, type PassoEscada } from "../types";
import { idade } from "../lib/format";
import { EscadaMini } from "../ui/EscadaMini";
import { Logo } from "../ui/Logo";
import { getClinicaAtiva, agendamentosNoIntervalo } from "../agenda/queries";
import { chaveDia, hojeChave } from "../agenda/domain";
import { TIPO_LABEL } from "../agenda/types";

/**
 * Painel do profissional (Archetype E do design) — visão desktop/web.
 *
 * Sai do shell mobile de 560px (ocupa a tela toda) e dá ao profissional um
 * "cockpit": pacientes à esquerda, paciente ativo + ações no topo, e cartões de
 * leitura (de quem é a vez · pendências · módulos · esta semana · agenda). Lê o
 * banco local — nada de número inventado; cada card tem seu estado vazio.
 * No mobile, reflui para uma coluna (a sidebar vira uma faixa de pacientes).
 */

function inicioDaSemanaISO(): string {
  const now = new Date();
  const diff = (now.getDay() + 6) % 7; // dias desde segunda
  const seg = new Date(now);
  seg.setHours(0, 0, 0, 0);
  seg.setDate(now.getDate() - diff);
  return seg.toISOString();
}

function Avatar({ texto, tam = 40, ativo }: { texto: string; tam?: number; ativo?: boolean }) {
  return (
    <span
      className="painel-avatar"
      aria-hidden
      style={{
        width: tam,
        height: tam,
        fontSize: tam * 0.42,
        background: ativo ? "var(--accent-suave-bg)" : "var(--surface-2)",
        color: ativo ? "var(--accent-forte)" : "var(--text-muted)",
      }}
    >
      {texto.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

export function PainelProfissional() {
  const nav = useNavigate();
  const cfg = useLiveQuery(getConfig);
  const criancas = useLiveQuery(() => db.crianca.toArray()) ?? [];
  const [busca, setBusca] = useState("");

  const ativa =
    criancas.find((c) => c.id === cfg?.crianca_ativa_id) ?? criancas[0] ?? null;
  const ativaId = ativa?.id;

  // dados do paciente ativo (uma query por tabela, indexada por crianca_id)
  const ofertas = useLiveQuery(
    async () => (ativaId ? db.oferta.where("crianca_id").equals(ativaId).toArray() : []),
    [ativaId],
  ) ?? [];
  const notas = useLiveQuery(
    async () => (ativaId ? db.nota.where("crianca_id").equals(ativaId).toArray() : []),
    [ativaId],
  ) ?? [];
  const momentos = useLiveQuery(
    async () => (ativaId ? db.momento.where("crianca_id").equals(ativaId).toArray() : []),
    [ativaId],
  ) ?? [];
  const momentosPsm = useLiveQuery(
    async () => (ativaId ? db.momento_psm.where("crianca_id").equals(ativaId).toArray() : []),
    [ativaId],
  ) ?? [];
  const alimentos = useLiveQuery(() => db.alimento.toArray()) ?? [];

  // agenda (se ativada): próximos 7 dias da clínica
  const clinica = useLiveQuery(
    async () => (cfg?.agenda_ativa ? getClinicaAtiva() : undefined),
    [cfg?.agenda_ativa],
  );
  const proximos = useLiveQuery(async () => {
    if (!clinica?.id) return [];
    const de = hojeChave();
    const ate = chaveDia(new Date(Date.now() + 7 * 864e5));
    return agendamentosNoIntervalo(clinica.id, de, ate);
  }, [clinica?.id]) ?? [];

  const modulos = useMemo(
    () => [
      { key: "pitada", emoji: "🍓", nome: "pitada", sub: "alimentar", on: ofertas.length > 0, to: "/degrau" },
      { key: "compasso", emoji: "🎵", nome: "compasso", sub: "musicoterapia", on: momentos.length > 0, to: "/compasso" },
      { key: "gingado", emoji: "🤸", nome: "gingado", sub: "psicomotricidade", on: momentosPsm.length > 0, to: "/gingado" },
      { key: "elo", emoji: "🔗", nome: "elo", sub: "integração", on: notas.length > 0, to: "/elo" },
    ],
    [ofertas.length, momentos.length, momentosPsm.length, notas.length],
  );
  const emojisAtivos = modulos.filter((m) => m.on).map((m) => m.emoji).join("");

  // "de quem é a vez": alimentos em teste que este paciente já experimentou
  const vez = useMemo(() => {
    const emTeste = alimentos.filter((a) => a.status === "em_teste" && !a.arquivado);
    return emTeste
      .map((a) => {
        const ofs = ofertas
          .filter((o) => o.alimento_id === a.id)
          .sort((x, y) => y.data_hora.localeCompare(x.data_hora));
        return { a, passo: ofs[0]?.passo_escada ?? null, ultima: ofs[0]?.data_hora ?? null };
      })
      .filter((v) => v.ultima)
      .sort((x, y) => (y.ultima as string).localeCompare(x.ultima as string))
      .slice(0, 5);
  }, [alimentos, ofertas]);

  // pendências: ofertas da criança aguardando confirmação do cuidador
  const pendentes = useMemo(() => ofertas.filter((o) => o.confirmado === false), [ofertas]);

  // esta semana (segunda → agora), por tabela
  const semana = useMemo(() => {
    const ini = inicioDaSemanaISO();
    const cont = (arr: { data_hora: string }[]) => arr.filter((x) => x.data_hora >= ini).length;
    const contNota = notas.filter((n) => n.data_sessao >= ini).length;
    return {
      ofertas: cont(ofertas),
      sessoes: contNota,
      momentos: cont(momentos) + cont(momentosPsm),
    };
  }, [ofertas, notas, momentos, momentosPsm]);

  const listaFiltrada = criancas.filter((c) =>
    (c.apelido + " " + (c.nome_completo ?? "")).toLowerCase().includes(busca.toLowerCase()),
  );

  function abrirPaciente(id: string) {
    void salvarConfig({ crianca_ativa_id: id });
  }

  const cpfMasc = ativa?.responsavel_cpf
    ? `•••.•••.•••-${ativa.responsavel_cpf.replace(/\D/g, "").slice(-2) || "••"}`
    : null;

  // exclusivo do profissional: sem a flag ligada, não acessa o painel (deep link)
  if (cfg && !cfg.perfil_profissional) return <Navigate to="/" replace />;

  return (
    <div className="painel">
      {/* ───────── sidebar: pacientes ───────── */}
      <aside className="painel-side">
        <div className="painel-side-cab">
          <Logo size={34} />
          <div>
            <div className="painel-side-nome">Degrau</div>
            <div className="painel-side-sub">painel do profissional</div>
          </div>
        </div>

        <label className="painel-busca">
          <span aria-hidden>🔎</span>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="buscar paciente"
          />
        </label>

        <div className="painel-side-titulo">pacientes</div>
        <div className="painel-side-lista">
          {listaFiltrada.length === 0 ? (
            <p className="painel-side-vazio">
              {criancas.length === 0 ? "nenhum paciente ainda." : "ninguém encontrado."}
            </p>
          ) : (
            listaFiltrada.map((c) => {
              const on = c.id === ativaId;
              return (
                <button
                  key={c.id}
                  className={`painel-paciente ${on ? "on" : ""}`}
                  onClick={() => abrirPaciente(c.id)}
                >
                  <Avatar texto={c.apelido} tam={38} ativo={on} />
                  <span className="painel-paciente-corpo">
                    <span className="painel-paciente-nome">
                      {c.apelido}
                      {idade(c.data_nascimento) !== null && (
                        <span className="painel-suave"> · {idade(c.data_nascimento)} anos</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <button className="btn btn-bloco" onClick={() => nav("/paciente?novo=1")}>
          + cadastrar paciente
        </button>
      </aside>

      {/* ───────── main ───────── */}
      <main className="painel-main">
        <button className="painel-voltar" onClick={() => nav("/")} aria-label="voltar aos módulos">
          ← módulos
        </button>

        {!ativa ? (
          <div className="vazio" style={{ marginTop: "var(--s7)" }}>
            <span className="emoji" aria-hidden>👋</span>
            cadastre o primeiro paciente para começar.
            <div style={{ marginTop: "var(--s3)" }}>
              <Link to="/paciente" className="btn btn-primario">cadastrar paciente</Link>
            </div>
          </div>
        ) : (
          <>
            {/* header do paciente */}
            <div className="painel-header">
              <Avatar texto={ativa.apelido} tam={50} ativo />
              <div className="crescer">
                <h1 className="painel-paciente-titulo">
                  {ativa.apelido}
                  <span className="painel-suave">
                    {idade(ativa.data_nascimento) !== null ? ` · ${idade(ativa.data_nascimento)} anos` : ""}
                    {emojisAtivos ? ` · ${emojisAtivos}` : ""}
                  </span>
                </h1>
                <div className="painel-meta">
                  {modulos.filter((m) => m.on).length} módulo(s) com registros
                </div>
              </div>
              <div className="painel-acoes">
                <Link to="/elo/registrar" className="btn btn-primario">nova nota</Link>
                <Link to="/elo/timeline" className="btn">timeline</Link>
                <Link to="/relatorio" className="btn" aria-label="relatório pdf">PDF</Link>
              </div>
            </div>

            {/* duas colunas */}
            <div className="painel-grid">
              {/* esquerda */}
              <div className="painel-col">
                <section className="card painel-card">
                  <div className="painel-card-cab">
                    <h2>de quem é a vez</h2>
                    <span className="painel-suave">o que está em teste agora</span>
                  </div>
                  {vez.length === 0 ? (
                    <p className="painel-vazio-card">nada em teste no momento.</p>
                  ) : (
                    <div className="painel-linhas">
                      {vez.map((v) => {
                        const passo = v.passo as PassoEscada | null;
                        const prox = passo !== null && passo < 9 ? ((passo + 1) as PassoEscada) : null;
                        return (
                          <Link key={v.a.id} to={`/alimento/${v.a.id}`} className="painel-linha">
                            <span className="painel-linha-corpo">
                              <span className="painel-linha-nome">{v.a.nome}</span>
                              <span className="painel-linha-sub">
                                <EscadaMini passo={passo} />
                                {prox !== null
                                  ? `próximo: ${PASSO_ESCADA_LABEL[prox]}`
                                  : "no topo da escada"}
                              </span>
                            </span>
                            <span aria-hidden className="painel-chevron">›</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section className="card painel-card">
                  <div className="painel-card-cab">
                    <h2>pendências</h2>
                  </div>
                  <div className="painel-linhas">
                    {pendentes.length > 0 && (
                      <Link to="/degrau" className="painel-linha">
                        <span className="painel-pend-ic" aria-hidden>✓</span>
                        <span className="painel-linha-corpo">
                          <span className="painel-linha-nome">
                            {pendentes.length} escolha(s) de {ativa.apelido} aguardando confirmação
                          </span>
                        </span>
                        <span aria-hidden className="painel-chevron">›</span>
                      </Link>
                    )}
                    {cfg?.agenda_ativa && proximos.length > 0 && (
                      <Link to="/agenda" className="painel-linha">
                        <span className="painel-pend-ic" aria-hidden>🗓️</span>
                        <span className="painel-linha-corpo">
                          <span className="painel-linha-nome">
                            {proximos.length} consulta(s) nos próximos 7 dias
                          </span>
                        </span>
                        <span aria-hidden className="painel-chevron">›</span>
                      </Link>
                    )}
                    {pendentes.length === 0 && !(cfg?.agenda_ativa && proximos.length > 0) && (
                      <p className="painel-vazio-card">nada pendente. tudo em dia.</p>
                    )}
                  </div>
                </section>
              </div>

              {/* direita */}
              <div className="painel-col">
                <section className="card painel-card">
                  <div className="painel-card-cab">
                    <h2>módulos</h2>
                  </div>
                  <div className="painel-modulos">
                    {modulos.map((m) => (
                      <button
                        key={m.key}
                        className={`painel-modulo ${m.on ? "on" : ""}`}
                        onClick={() => nav(m.to)}
                      >
                        <span className="painel-modulo-emoji" aria-hidden>{m.emoji}</span>
                        <span className="crescer">
                          <span className="painel-modulo-nome">{m.nome}</span>
                          <span className="painel-suave"> {m.sub}</span>
                        </span>
                        <span className="painel-modulo-estado">
                          {m.on ? "com registros" : "abrir"}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="card painel-card">
                  <div className="painel-card-cab">
                    <h2>esta semana</h2>
                  </div>
                  <div className="painel-stats">
                    {[
                      ["ofertas registradas", semana.ofertas],
                      ["sessões anotadas", semana.sessoes],
                      ["momentos (música/corpo)", semana.momentos],
                    ].map(([k, v]) => (
                      <div className="painel-stat" key={k as string}>
                        <span className="painel-suave">{k}</span>
                        <span className="painel-stat-num">{v as number}</span>
                      </div>
                    ))}
                  </div>
                  <p className="painel-suave" style={{ fontSize: "0.78rem", marginTop: "var(--s2)" }}>
                    resumo da participação — não é desempenho nem nota.
                  </p>
                </section>

                {cfg?.agenda_ativa && (
                  <section className="card painel-card">
                    <div className="painel-card-cab">
                      <h2>agenda</h2>
                      <Link to="/agenda" className="painel-suave">abrir calendário ›</Link>
                    </div>
                    {proximos.length === 0 ? (
                      <p className="painel-vazio-card">sem consultas nos próximos 7 dias.</p>
                    ) : (
                      <div className="painel-linhas">
                        {proximos
                          .slice()
                          .sort((a, b) => (a.data + a.hora_inicio).localeCompare(b.data + b.hora_inicio))
                          .slice(0, 4)
                          .map((a) => (
                            <Link key={a.id} to={`/agenda/${a.id}`} className="painel-linha">
                              <span className="painel-linha-corpo">
                                <span className="painel-linha-nome">{a.paciente_nome}</span>
                                <span className="painel-linha-sub">
                                  {a.data.slice(8)}/{a.data.slice(5, 7)} · {a.hora_inicio} · {TIPO_LABEL[a.tipo]}
                                </span>
                              </span>
                              <span aria-hidden className="painel-chevron">›</span>
                            </Link>
                          ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>

            {/* rodapé: responsável + export (CPF no fim — princípio 6) */}
            <div className="painel-rodape">
              <span className="painel-suave">
                {ativa.responsavel_nome ? `responsável: ${ativa.responsavel_nome}` : "sem responsável cadastrado"}
                {cpfMasc ? ` · cpf ${cpfMasc}` : ""}
              </span>
              <span className="painel-rodape-acoes">
                <Link to="/relatorio" className="painel-suave">PDF clínico</Link>
                <Link to="/config" className="painel-suave">configurações</Link>
              </span>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
